import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Download, Mail, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { api } from '../lib/api';
import { useToast } from '../hooks/use-toast';
import { trackLeadMagnet } from '../lib/analytics';

export default function LeadMagnet() {
  const [email, setEmail] = useState('');
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [bWebsite, setBWebsite] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await api.leadMagnet({
        email,
        marketing_consent: String(marketingConsent),
        consentTimestamp: new Date().toISOString(),
        b_website: bWebsite
      });
      // Track lead magnet download
      trackLeadMagnet('Free Wealth Building Guide');
      
      if (res?.data?.alreadyDelivered) {
        toast({
          title: "Blueprint Already Delivered!",
          description: "Your Financial Freedom Blueprint has already been delivered to this email. We've resent another copy to your inbox just in case!",
          variant: "default"
        });
      } else {
        toast({
          title: "Success!",
          description: "Thanks! Check your inbox for your Financial Freedom Blueprint.",
          variant: "success"
        });
      }
      setEmail('');
      setMarketingConsent(false);
    } catch (err) {
      toast({
        title: "Error",
        description: err.message || 'Submission failed. Please try again.',
        variant: "destructive"
      });
    } finally {
      setSubmitting(false);
    }
  }
  return (
    <section id="lead-magnet" className="relative py-24 bg-[#070E1B] text-white overflow-hidden">
      {/* ── Background Mesh Light Flares ───────────────── */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden z-0">
        <div className="absolute top-1/2 -left-40 w-[500px] h-[500px] bg-[#C9A84C]/10 rounded-full blur-[150px] animate-pulse-glow" />
        <div className="absolute -bottom-20 right-10 w-[500px] h-[500px] bg-[#3D6ACC]/15 rounded-full blur-[150px]" />
        <div 
          className="absolute inset-0 opacity-[0.05]" 
          style={{
            backgroundImage: `radial-gradient(rgba(255, 255, 255, 0.4) 1px, transparent 1px)`,
            backgroundSize: '32px 32px'
          }}
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16 space-y-4">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#162236] border border-[#C9A84C]/30 text-xs sm:text-sm font-semibold text-[#E8D4A0] tracking-wider uppercase">
            <Download className="w-4 h-4 text-[#C9A84C]" />
            Free Wealth Guide • Instant Access
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-playfair tracking-tight">
            Unlock The <span className="text-gradient-gold">Tax-Free Wealth Blueprint</span>
          </h2>
          <p className="text-lg sm:text-xl text-slate-300 max-w-3xl mx-auto font-opensans leading-relaxed">
            Discover how high-net-worth families compound capital tax-free, protect assets from market crashes, and secure guaranteed lifetime income.
          </p>
        </div>
        
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          {/* Left side - Benefits List */}
          <div className="lg:col-span-6 space-y-8">
            <div className="space-y-6 bg-[#0D1B2A]/80 border border-[#C9A84C]/20 p-8 rounded-2xl backdrop-blur-xl shadow-2xl">
              <h3 className="text-2xl font-bold font-playfair text-white flex items-center gap-3">
                <span className="w-2 h-8 bg-gradient-to-b from-[#C9A84C] to-[#8C6D23] rounded-full inline-block" />
                What You'll Discover Inside:
              </h3>

              <div className="space-y-5">
                <div className="flex items-start gap-4">
                  <div className="p-2.5 rounded-xl bg-[#162236] border border-[#C9A84C]/30 text-[#C9A84C] shrink-0 mt-0.5">
                    <CheckCircle className="h-5 w-5" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-base text-white">Tax-Free Cash Accumulation (IRC Sec. 7702)</h4>
                    <p className="text-sm text-slate-300 leading-relaxed mt-1">Learn how to compound wealth 100% tax-free and access liquidity without triggering tax liabilities.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="p-2.5 rounded-xl bg-[#162236] border border-[#C9A84C]/30 text-[#C9A84C] shrink-0 mt-0.5">
                    <CheckCircle className="h-5 w-5" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-base text-white">Living Benefits & Asset Protection</h4>
                    <p className="text-sm text-slate-300 leading-relaxed mt-1">Safeguard your family with policies that pay out during critical, chronic, or terminal health events while living.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="p-2.5 rounded-xl bg-[#162236] border border-[#C9A84C]/30 text-[#C9A84C] shrink-0 mt-0.5">
                    <CheckCircle className="h-5 w-5" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-base text-white">Guaranteed 0% Market Downside Protection</h4>
                    <p className="text-sm text-slate-300 leading-relaxed mt-1">Capture market gains in up-years while locking in a contractual 0% floor during market corrections.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="p-2.5 rounded-xl bg-[#162236] border border-[#C9A84C]/30 text-[#C9A84C] shrink-0 mt-0.5">
                    <CheckCircle className="h-5 w-5" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-base text-white">Personalized Financial Needs Analysis (FNA)</h4>
                    <p className="text-sm text-slate-300 leading-relaxed mt-1">Understand your exact financial gap and get a step-by-step roadmap tailored to your family's future.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right side - Lead Capture Form */}
          <div className="lg:col-span-6">
            <div className="bg-[#162236]/90 border border-[#C9A84C]/30 rounded-2xl p-8 sm:p-10 shadow-2xl backdrop-blur-xl relative">
              <div className="text-center space-y-3 mb-8">
                <div className="mx-auto w-14 h-14 rounded-2xl bg-gradient-to-br from-[#C9A84C] to-[#8C6D23] flex items-center justify-center shadow-lg">
                  <Download className="h-7 w-7 text-[#070E1B]" />
                </div>
                <h3 className="text-2xl font-bold font-playfair text-white">Get Instant PDF Download</h3>
                <p className="text-sm text-slate-300">
                  Enter your email below to receive immediate access to the PDF guide in your inbox.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Hidden Honeypot Field for Bot Prevention */}
                <div style={{ display: 'none' }} aria-hidden="true">
                  <input 
                    type="text" 
                    name="b_website" 
                    tabIndex={-1} 
                    autoComplete="off" 
                    value={bWebsite} 
                    onChange={(e) => setBWebsite(e.target.value)} 
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold uppercase text-slate-300 tracking-wider mb-2">
                    Email Address
                  </label>
                  <Input 
                    type="email" 
                    placeholder="Enter your best email address..." 
                    className="h-13 bg-[#0D1B2A] border border-[#C9A84C]/30 text-white placeholder-slate-400 focus:border-[#C9A84C] focus:ring-1 focus:ring-[#C9A84C] rounded-xl text-base px-4"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
                
                {/* Privacy Consent Checkboxes */}
                <div className="space-y-3 pt-1">
                  <div className="flex items-start gap-3">
                    <input 
                      type="checkbox" 
                      id="privacy-consent-lead" 
                      required
                      className="mt-1 h-4 w-4 rounded accent-[#C9A84C] bg-[#0D1B2A] border-[#C9A84C]/40"
                    />
                    <label htmlFor="privacy-consent-lead" className="text-xs text-slate-300 leading-relaxed">
                      I consent to receiving financial education content and updates from Grow & Stay Rich. I can unsubscribe anytime. 
                      {" "}<Link to="/privacy" className="text-[#C9A84C] hover:underline">Privacy Policy</Link>
                    </label>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <input 
                      type="checkbox" 
                      id="marketing-consent-lead" 
                      className="mt-1 h-4 w-4 rounded accent-[#C9A84C] bg-[#0D1B2A] border-[#C9A84C]/40"
                      checked={marketingConsent}
                      onChange={(e) => setMarketingConsent(e.target.checked)}
                    />
                    <label htmlFor="marketing-consent-lead" className="text-xs text-slate-300 leading-relaxed">
                      Send me exclusive financial strategies and invite-only masterclass alerts (optional).
                    </label>
                  </div>
                </div>
                
                <Button 
                  type="submit" 
                  disabled={submitting} 
                  className="w-full h-14 text-base font-bold bg-gradient-gold-cta text-[#070E1B] rounded-xl shadow-xl hover:opacity-95 transition-all duration-300 flex items-center justify-center gap-2"
                >
                  <Mail className="h-5 w-5" />
                  {submitting ? 'Sending Your Guide…' : 'Claim Free Wealth Guide'}
                </Button>
              </form>
              
              {/* GDPR Security Badge */}
              <div className="mt-6 pt-6 border-t border-slate-700/60 flex items-center justify-between text-xs text-slate-400">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-emerald-400" />
                  <span className="font-medium text-emerald-300">100% Privacy Protected</span>
                </div>
                <span>No Spam • Unsubscribe Anytime</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}